import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Lightbulb, AlertTriangle, Sparkles, ArrowRight, CalendarClock, ClipboardList, Check } from "lucide-react";

export default function ForecastInsightDialog({ projectId, task, onOpenChange, onOpenTask }) {
  const [data, setData] = useState(null);
  const [tipState, setTipState] = useState({});
  const open = !!task;

  useEffect(() => {
    if (!task) { setData(null); setTipState({}); return; }
    api.get(`/projects/${projectId}/forecast-insight/${task.task_id}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ drivers: ["Could not load the analysis."], tips: [], insight: "" }));
  }, [task, projectId]);

  const tipToAction = async (tip, i) => {
    setTipState((s) => ({ ...s, [i]: "saving" }));
    try {
      const { data: item } = await api.post(`/projects/${projectId}/action-items`, {
        title: tip.length > 140 ? `${tip.slice(0, 137)}…` : tip,
        description: `From Auto Pilot forecast — at-risk task "${task.title}" (due ${task.end_date || "—"}, forecast ${task.predicted_finish}).\n\n${tip}`,
        priority: "high", status: "open", source: "general", linked_task_id: task.task_id,
      });
      setTipState((s) => ({ ...s, [i]: "done" }));
      toast.success(`Action item ${item.display_id || ""} created from Maestro's tip`);
    } catch {
      setTipState((s) => ({ ...s, [i]: undefined }));
      toast.error("Couldn't create the action item — please try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="forecast-insight-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <span className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center shrink-0"><AlertTriangle className="h-4 w-4 text-red-600" /></span>
            At risk: {task?.title}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> due {task?.end_date || "—"}</span>
            <span className="text-red-600 font-semibold">forecast {task?.predicted_finish}</span>
            <span>· {task?.percent_complete}% complete</span>
          </DialogDescription>
        </DialogHeader>

        {!data ? (
          <div className="flex items-center gap-2 py-8 justify-center text-sm text-muted-foreground" data-testid="insight-loading">
            <Loader2 className="h-4 w-4 animate-spin text-primary" /> Maestro is analyzing this task…
          </div>
        ) : (
          <div className="space-y-4">
            <div data-testid="insight-drivers">
              <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5">Why Pilot flagged it</p>
              <ul className="space-y-1.5">
                {(data.drivers || []).map((d, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="h-1.5 w-1.5 rounded-full bg-red-500 mt-1.5 shrink-0" />{d}
                  </li>
                ))}
              </ul>
            </div>
            {data.insight && (
              <div className="rounded-lg border border-violet-200 bg-violet-50 p-3" data-testid="insight-ai">
                <p className="text-xs font-bold uppercase tracking-wide text-violet-700 mb-1 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Maestro's read</p>
                <p className="text-sm text-violet-950">{data.insight}</p>
              </div>
            )}
            <div data-testid="insight-tips">
              <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center gap-1.5"><Lightbulb className="h-3.5 w-3.5 text-amber-500" /> How to get it back on track</p>
              <ol className="space-y-2">
                {(data.tips || []).map((t, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm rounded-lg border border-border bg-secondary/30 px-3 py-2" data-testid={`insight-tip-${i}`}>
                    <span className="shrink-0 h-5 w-5 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center mt-px">{i + 1}</span>
                    <span className="flex-1">{t}</span>
                    {tipState[i] === "done" ? (
                      <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 mt-px" data-testid={`insight-tip-created-${i}`}><Check className="h-3.5 w-3.5" /> Added</span>
                    ) : (
                      <button type="button" onClick={() => tipToAction(t, i)} disabled={tipState[i] === "saving"}
                        title="Turn this tip into an action item"
                        className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-primary hover:underline mt-px disabled:opacity-50"
                        data-testid={`insight-tip-action-${i}`}>
                        {tipState[i] === "saving" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ClipboardList className="h-3.5 w-3.5" />} Action item
                      </button>
                    )}
                  </li>
                ))}
              </ol>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="insight-close">Close</Button>
              <Button size="sm" className="gap-1.5" onClick={() => onOpenTask(task.task_id)} data-testid="insight-open-task">
                Open the task <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
